import { useTheme } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import {
    type AddArticleParamsType,
    type DossierArticle,
    DossierRoutingContext,
    DossierValidatorStatus,
    REFRESH_CURRENT_DOSSIER_CTX,
    RESET_DOS_DOCS_CTX,
    VALIDATOR_TYPE_IMPOTS,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierArticle,
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    useDossierValidatorsCtx,
} from '@europrocurement/l2d-modules';
import {
    Accordion,
    ConfirmationModal,
    OptionType,
    useModal,
} from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useNavigate } from 'react-router';
import { NestedDossierProcessesOpenSearch } from '@europrocurement/l2d-domain';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierProcessesFooter from '../DossierProcessesFooter/DossierProcessesFooter';
import AddTaxForm, { AddTaxFormData } from '../AddTaxForm/AddTaxForm';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import DossierArticlesProcessesTaxList from '../DossierArticlesProcessesTaxList/DossierArticlesProcessesTaxList';
import { DossierProcessesArticlesProps } from '../DossierProcesses/DossierProcessesType';
import DissociateFormalitiesForm from '../DissociateFormalitiesForm/DissociateFormalitiesForm';
import MoveFormalitiesForm from '../MoveFormalitiesToOtherDossierForm/MoveFormalitiesToOtherDossierForm';
import DossierProcessesBulkActionsBar from '../DossierProcessesBulkActionsBar/DossierProcessesBulkActionsBar';

const DossierImpotProcesses: React.FunctionComponent<DossierProcessesArticlesProps> = function ({
    articles,
}) {
    const validatorTypeId = VALIDATOR_TYPE_IMPOTS;
    const {
        getArticleProcessesStatus,
        getArticleProcessesStatusLabel,
        getArticleProcessesStatusColor,
        findProcessByArticle,
        canCreateValidatorProcess,
        createValidatorProcess,
        getProcessStatusByArticle,
        hasOnGoingProcesses,
    } = useDossierProcesses();
    const { palette } = useTheme();
    const { addArticle, deleteArticle } = useDossierArticle();
    const { dossier, siblings } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();
    const { getDossierDocumentsWithFiles } = useDossierDocumentsCtx();
    const dossierDocumentsdispatch = useDossierDocumentsCtxDispatch();
    const navigate = useNavigate();
    const { getDossierRoute } = React.useContext(DossierRoutingContext);

    const refreshDossier = useCallback(
        (dossierId?: number) => {
            if (dispatch) {
                dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
            }
            if (dossierDocumentsdispatch) {
                dossierDocumentsdispatch({ type: RESET_DOS_DOCS_CTX });
            }
            if (dossierId) {
                const route = getDossierRoute(dossierId as number);
                if (route) {
                    navigate(route);
                }
            }
        },
        [dispatch, dossierDocumentsdispatch, getDossierRoute, navigate],
    );

    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();
    const { getDossierValidatorById } = useDossierValidatorsCtx();

    const [processing, setProcessing] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const filteredSiblings =
        (siblings &&
            dossier &&
            siblings.filter((d) => d.id !== dossier.id && d.deleted === false)) ||
        [];

    const status = useMemo(
        () =>
            dossier
                ? getArticleProcessesStatus(
                      dossier,
                      validatorTypeId,
                      articles.map((article) => article.id),
                  )
                : 'ongoing',
        [dossier, validatorTypeId, articles, getArticleProcessesStatus],
    );

    const { actionUpdateValidatorStatus, handleUpdateStatusChange } =
        useDossierProcessesActionsAndRender();
    const handelDeleteArticle = (articleId: string) => {
        setProcessing(true);

        deleteArticle(articleId)
            .then(() => {
                setProcessing(false);
                modalActions.close();
            })
            .catch((err) =>
                apiErrorMessageSnackbar(
                    err,
                    'Une erreur est survenue lors de la suppression de la démarche',
                ),
            );

        setProcessing(false);
        refreshDossier();
    };

    const handleStatusChange = async (
        process: NestedDossierProcessesOpenSearch,
        newStatus: DossierValidatorStatus,
    ) => {
        try {
            await handleUpdateStatusChange(process, newStatus.code);
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la mise à jour de votre démarche', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
            refreshDossier();
        }
    };

    const canCreateProcess = useMemo(
        () => (dossier ? canCreateValidatorProcess(dossier, validatorTypeId) : false),
        [dossier, canCreateValidatorProcess, validatorTypeId],
    );

    const getItemActions = (item: DossierArticle) => {
        if (!dossier) {
            return [];
        }
        const actions: Array<DossierArticleProcessMenuItemProps> = [];
        const statusProcess = getProcessStatusByArticle(dossier, validatorTypeId, item.id);
        const process = findProcessByArticle(dossier, [validatorTypeId], item.id);
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];

        if (['pending'].includes(statusProcess) && canCreateProcess) {
            if (process) {
                actions.push(
                    actionUpdateValidatorStatus(() => {
                        modalActions.call(
                            <UpdateValidatorForm
                                processing={processing}
                                process={process}
                                onStatusChanged={(newStatus) => {
                                    handleStatusChange(process, newStatus);
                                }}
                                statuses={statuses}
                            />,
                        );
                    }),
                );
            }

            actions.push({
                label: 'Supprimer la démarche',
                icon: 'trash',
                onClick: (article: DossierArticle) => {
                    modalActions.call(
                        <ConfirmationModal
                            color={palette.error}
                            messages={{
                                question: 'Confirmer cette action ?',
                                detail: {
                                    message: 'Êtes vous sur de vouloir supprimer la démarche ?',
                                    severity: 'error',
                                },
                                validation: 'Supprimer',
                            }}
                            actionOnValidation={() => handelDeleteArticle(article.id.toString())}
                            actionOnCancellation={() => modalActions.close()}
                        />,
                    );
                },
            });
        } else if (['ongoing', 'default', 'rescheduled'].includes(statusProcess)) {
            actions.push({
                label: 'Demander une signature électronique',
                icon: 'file-signature',
                onClick: (article: DossierArticle) => {
                    enqueueSnackbar(`Non implémenté signature ${article.label}`, {
                        variant: 'error',
                    });
                },
            });

            actions.push({
                label: 'Activer option DCE',
                icon: 'check-square',
                onClick: (article: DossierArticle) => {
                    enqueueSnackbar(`Non implémenté option DCE ${article.label}`, {
                        variant: 'error',
                    });
                },
            });

            if (process) {
                actions.push(
                    actionUpdateValidatorStatus(() => {
                        modalActions.call(
                            <UpdateValidatorForm
                                processing={processing}
                                process={process}
                                onStatusChanged={(newStatus) => {
                                    handleStatusChange(process, newStatus);
                                }}
                                statuses={statuses}
                            />,
                        );
                    }),
                );
            }
        }
        return actions;
    };

    const getFormalityIdsFromArticleIds = (articleIds: number[]): number[] => {
        if (!dossier) return [];
        const formalityIds = new Set<number>();
        articleIds.forEach((articleId) => {
            const formality = dossier.formalites.find((f) => f.articleIds.includes(articleId));
            if (formality?.id != null) {
                formalityIds.add(formality.id);
            }
        });
        return Array.from(formalityIds);
    };

    const handleDissociate = () => {
        if (!dossier) {
            return;
        }
        if (hasOnGoingProcesses(dossier.validationProcesses)) {
            enqueueSnackbar(
                'Des processus de validation sont en cours sur le dossier, dissociation impossible',
                { variant: 'error' },
            );
            return;
        }
        modalActions.call(
            <DissociateFormalitiesForm
                formalitiesIds={getFormalityIdsFromArticleIds(selectedIds)}
                dossier={dossier}
                documents={getDossierDocumentsWithFiles()}
                onSubmitDone={refreshDossier}
            />,
        );
    };

    const handleMove = () => {
        if (!dossier) {
            return;
        }
        if (hasOnGoingProcesses(dossier.validationProcesses)) {
            enqueueSnackbar(
                'Des processus de validation sont en cours sur le dossier, déplacement impossible',
                { variant: 'error' },
            );
            return;
        }
        const options = filteredSiblings.map((doss) => {
            const nbrFormalities = doss.formalites.length;
            return {
                value: doss.id,
                label: `${doss.numero} - ${doss.societeDenomination} - ${
                    nbrFormalities > 1
                        ? `${doss.formalites[0].libelle} + ${nbrFormalities - 1} `
                        : doss.formalites[0].libelle
                }`,
            } as OptionType;
        });
        modalActions.call(
            <MoveFormalitiesForm
                formalitiesIds={getFormalityIdsFromArticleIds(selectedIds)}
                dossier={dossier}
                documents={getDossierDocumentsWithFiles()}
                onSubmitDone={refreshDossier}
                options={options}
            />,
        );
    };

    const onSubmitAddTax = async (data: AddTaxFormData) => {
        if (!dossier) {
            enqueueSnackbar('Dossier non trouvé', { variant: 'error' });
            return;
        }
        setProcessing(true);

        try {
            const articleId = await addArticle({
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
            } as AddArticleParamsType);
            await createValidatorProcess(dossier.id as number, [articleId]);
            modalActions.close();
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'ajout de l'enregistrement",
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleAddTax = useCallback(() => {
        if (!dossier) {
            enqueueSnackbar('Dossier non trouvé', { variant: 'error' });
            return;
        }
        modalActions.call(
            <AddTaxForm
                onCancel={() => {
                    setProcessing(false);
                    modalActions.close();
                }}
                onSubmit={onSubmitAddTax}
                dossier={dossier}
            />,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier, modalActions, processing]);

    return (
        <Accordion
            title="Enregistrements impôts"
            expandLabel={getArticleProcessesStatusLabel(status)}
            expanded
            expandColor={getArticleProcessesStatusColor(status)}
            sx={{ backgroundColor: palette.background.paper }}
            contentSxProps={{ gap: '16px', display: 'grid' }}
        >
            {dossier && (
                <>
                    <DossierProcessesBulkActionsBar
                        selectedIds={selectedIds}
                        onClearSelection={() => setSelectedIds([])}
                        canDissociate={(dossier.formalites?.length ?? 0) > 1}
                        onDissociate={handleDissociate}
                        canMove={filteredSiblings.length > 0}
                        onMove={handleMove}
                    />
                    <DossierArticlesProcessesTaxList
                        articles={articles}
                        dossier={dossier}
                        validatorTypeId={validatorTypeId}
                        legalAnnoucements={[]}
                        getItemActions={getItemActions}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                    />
                </>
            )}

            {canCreateProcess && (
                <DossierProcessesFooter
                    handleAddItem={handleAddTax}
                    addItemTextBtn="Ajouter un enregistrement"
                    canAddItem
                />
            )}
        </Accordion>
    );
};

export default DossierImpotProcesses;
